class CookiePanel {
  constructor() {
    // DOM элементы
    this.domElements = this.initializeDOMElements();

    // Состояние
    this.isOpened = false;
    this.animationTimeouts = new Set();

    // Константы для анимации
    this.ANIMATION_DELAYS = Object.freeze({
      CONTAINER: 50,
      ITEM_START: 150,
      ITEM_STEP: 100,
      CLOSE_STEP: 50,
      CLOSE_CONTAINER: 100,
      CLOSE_HIDE: 200,
      CLOSE_RESTORE: 300,
    });

    // API endpoints
    this.API_ENDPOINTS = Object.freeze({
      GET_SETTINGS: '/api/cookie-settings/get',
      SAVE_SETTINGS: '/api/cookie-settings',
    });

    this.init().catch(error => this.handleError('Ошибка инициализации cookie-панели', error));
  }

  initializeDOMElements() {
    const elements = {
      cookiePanel: document.querySelector('.cookie-panel'),
      cookiePanelInfo: document.querySelector('.cookie-panel-content__info'),
      cookiePanelInfoSettings: document.querySelector('.cookie-panel-content__info._settings'),
      settingsContainer: document.querySelector('.cookie-panel-content__settings'),
      settingsItems: document.querySelectorAll('.cookie-panel-content__settings-item'),
      settingsButton: document.querySelector('[data-cookie-toggle="settings"]'),
      acceptAllButton: document.querySelector('[data-cookie-action="accept-all"]'),
      closeSettingsButton: document.querySelector('[data-cookie-action="close-settings"]'),
      settingsText: document.querySelector('.cookie-settings-text'),
      settingsTextOpened: document.querySelector('.cookie-settings-text-opened'),
      openCookiePanel: document.querySelector('[data-cookie-action="open"]'),
    };

    // Проверяем наличие обязательных элементов
    const requiredElements = ['cookiePanel', 'settingsContainer'];
    requiredElements.forEach((elementName) => {
      if (!elements[elementName]) {
        throw new Error(`Обязательный элемент не найден: ${elementName}`);
      }
    });

    return elements;
  }

  handleError(message, error) {
    // eslint-disable-next-line no-console
    console.error(message, error);
  }

  async init() {
    this.bindEvents();
    await this.loadSavedSettingsToForm();

    const shouldShow = await this.shouldShowPanel();
    if (shouldShow) {
      this.showPanel();
    }
  }

  bindEvents() {
    const eventBindings = [
      {
        element: this.domElements.openCookiePanel,
        event: 'click',
        handler: this.handleOpenPanelClick.bind(this),
      },
      {
        element: this.domElements.settingsButton,
        event: 'click',
        handler: this.handleSettingsClick.bind(this),
      },
      {
        element: this.domElements.acceptAllButton,
        event: 'click',
        handler: this.handleAcceptAllClick.bind(this),
      },
      {
        element: this.domElements.closeSettingsButton,
        event: 'click',
        handler: this.handleCloseSettingsClick.bind(this),
      },
    ];

    eventBindings.forEach(({ element, event, handler }) => {
      if (element) {
        element.addEventListener(event, handler);
      }
    });
  }

  async loadSavedSettingsToForm() {
    try {
      const { settings: serverSettings } = await this.fetchSettingsFromServer();
      if (serverSettings) {
        this.applySavedSettingsToForm(serverSettings);
      }
    } catch (error) {
      this.handleError('Ошибка загрузки настроек cookie с сервера', error);
    }
  }

  async fetchSettingsFromServer() {
    const response = await fetch(this.API_ENDPOINTS.GET_SETTINGS, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'same-origin',
    });

    if (response.ok) {
      const data = await response.json();
      return data.success && data.settings ? {
        hasUserSettings: data.hasUserSettings, settings: data.settings,
      } : { hasUserSettings: false, settings: null };
    }

    return { hasUserSettings: false, settings: null };
  }

  applySavedSettingsToForm(settings) {
    if (!settings) return;

    const checkboxes = document.querySelectorAll('.cookie-panel input[type="checkbox"]');
    checkboxes.forEach((checkbox) => {
      const name = checkbox.name.replace(/^Cookie\[|\]$/g, '');
      if (Object.prototype.hasOwnProperty.call(settings, name)) {
        checkbox.checked = settings[name];
      }
    });
  }

  handleOpenPanelClick(e) {
    e.preventDefault();

    if (this.isOpened) {
      this.closeSettings();
    } else {
      this.showPanel();
      this.openSettings();
    }
  }

  handleSettingsClick(e) {
    e.preventDefault();

    if (!this.isOpened) {
      this.openSettings();
    } else {
      this.applyCookieSettings();
    }
  }

  handleAcceptAllClick(e) {
    e.preventDefault();
    this.acceptAllCookies();
  }

  handleCloseSettingsClick(e) {
    e.preventDefault();
    this.closeSettings();
  }

  clearAllTimeouts() {
    this.animationTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
    this.animationTimeouts.clear();
  }

  addTimeout(callback, delay) {
    const timeoutId = setTimeout(() => {
      callback();
      this.animationTimeouts.delete(timeoutId);
    }, delay);
    this.animationTimeouts.add(timeoutId);
    return timeoutId;
  }

  openSettings() {
    if (this.isOpened) return;
    this.isOpened = true;

    // Блокируем скролл страницы
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100%';

    this.clearAllTimeouts();

    // Скрываем информационный блок
    this.domElements.cookiePanelInfo.classList.remove('_visible', '_animated');

    // Показываем контейнеры настроек
    this.domElements.settingsContainer.classList.add('_visible');
    this.domElements.cookiePanelInfoSettings.classList.add('_visible');

    // Запускаем анимацию контейнера
    this.addTimeout(() => {
      this.domElements.settingsContainer.classList.add('_animated');
      this.domElements.cookiePanelInfoSettings.classList.add('_animated');
    }, this.ANIMATION_DELAYS.CONTAINER);

    // Запускаем каскадную анимацию элементов
    this.domElements.settingsItems.forEach((item, index) => {
      this.addTimeout(() => {
        item.classList.add('_animated');
      }, this.ANIMATION_DELAYS.ITEM_START + (index * this.ANIMATION_DELAYS.ITEM_STEP));
    });

    this.updateButtonText(true);
  }

  closeSettings() {
    if (!this.isOpened) return;
    this.isOpened = false;

    // Разблокируем скролл страницы
    document.body.style.overflow = '';
    document.body.style.height = '';

    this.clearAllTimeouts();

    // Убираем анимацию элементов (в обратном порядке)
    this.domElements.settingsItems.forEach((item, index) => {
      this.addTimeout(() => {
        item.classList.remove('_animated');
      }, index * this.ANIMATION_DELAYS.CLOSE_STEP);
    });

    // Убираем анимацию контейнера
    this.addTimeout(() => {
      this.domElements.settingsContainer.classList.remove('_animated');
      this.domElements.cookiePanelInfoSettings.classList.remove('_animated');
    }, this.domElements.settingsItems.length * this.ANIMATION_DELAYS.CLOSE_STEP + this.ANIMATION_DELAYS.CLOSE_CONTAINER);

    // Скрываем контейнер
    this.addTimeout(() => {
      this.domElements.settingsContainer.classList.remove('_visible');
      this.domElements.cookiePanelInfoSettings.classList.remove('_visible');
    }, this.domElements.settingsItems.length * this.ANIMATION_DELAYS.CLOSE_STEP + this.ANIMATION_DELAYS.CLOSE_HIDE);

    this.updateButtonText(false);

    // Возвращаем информационный блок
    this.addTimeout(() => {
      this.domElements.cookiePanelInfo.classList.add('_visible', '_animated');
    }, this.domElements.settingsItems.length * this.ANIMATION_DELAYS.CLOSE_STEP + this.ANIMATION_DELAYS.CLOSE_RESTORE);
  }

  async updateCheckboxState() {
    try {
      const { settings } = await this.fetchSettingsFromServer();
      if (!settings) return;

      Object.entries(settings).forEach(([key, value]) => {
        const checkbox = document.querySelector(`.cookie-panel input[name="Cookie[${key}]"]`);
        if (checkbox) {
          checkbox.checked = Boolean(value);
        }
      });
    } catch (error) {
      this.handleError('Ошибка получения настроек с сервера', error);
    }
  }

  updateButtonText(isSettingsOpened) {
    if (!this.domElements.settingsText || !this.domElements.settingsTextOpened) return;

    if (isSettingsOpened) {
      this.domElements.settingsText.style.display = 'none';
      this.domElements.settingsTextOpened.style.display = 'inline';
    } else {
      this.domElements.settingsText.style.display = 'inline';
      this.domElements.settingsTextOpened.style.display = 'none';
    }
  }

  getFormSettings() {
    const checkboxes = document.querySelectorAll('.cookie-panel input[type="checkbox"]');
    const settings = {};

    checkboxes.forEach((checkbox) => {
      if (checkbox.name && checkbox.name.startsWith('Cookie[')) {
        const name = checkbox.name.replace(/^Cookie\[|\]$/g, '');
        settings[name] = checkbox.checked;
      }
    });

    return settings;
  }

  applyCookieSettings() {
    const settings = this.getFormSettings();
    this.saveCookieSettings(settings);
    this.hideCookiePanel();
  }

  acceptAllCookies() {
    const settings = {
      'technical-cookies': true,
      'functional-cookies': true,
      'analytics-cookies': true,
    };

    this.saveCookieSettings(settings);
    this.hideCookiePanel();
  }

  async saveCookieSettings(settings = null) {
    const settingsToSave = settings || this.getFormSettings();

    // Получаем текущие настройки для сравнения analytics-cookies
    let currentAnalyticsCookies = null;
    try {
      const { settings: currentSettings } = await this.fetchSettingsFromServer();
      currentAnalyticsCookies = currentSettings ? currentSettings['analytics-cookies'] : null;
    } catch (error) {
      this.handleError('Ошибка получения текущих настроек', error);
    }

    try {
      const success = await this.saveSettingsToServer(settingsToSave);
      if (success) {
        this.dispatchCookieSettingsEvent(settingsToSave);

        // Проверяем, изменилась ли настройка analytics-cookies
        const newAnalyticsCookies = settingsToSave['analytics-cookies'];
        if (currentAnalyticsCookies !== null && currentAnalyticsCookies !== newAnalyticsCookies) {
          // Перезагружаем страницу если analytics-cookies изменилась
          window.location.reload();
        }

        return;
      }

      throw new Error('Не удалось сохранить настройки на сервере');
    } catch (error) {
      this.handleError('Ошибка сохранения настроек на сервере', error);
      // Показываем пользователю сообщение об ошибке
      this.showErrorMessage('Не удалось сохранить настройки. Попробуйте еще раз.');
    }
  }

  async saveSettingsToServer(settings) {
    const formData = new FormData();
    Object.entries(settings).forEach(([key, value]) => {
      formData.append(`Cookie[${key}]`, value ? '1' : '0');
    });

    // Добавляем URL текущей страницы
    formData.append('pageUrl', window.location.href);

    const response = await fetch(this.API_ENDPOINTS.SAVE_SETTINGS, {
      method: 'POST',
      credentials: 'same-origin',
      body: formData,
    });

    if (response.ok) {
      const data = await response.json();
      return data.success;
    }

    return false;
  }

  showErrorMessage(message) {
    // Простая реализация показа ошибки (можно расширить)
    // eslint-disable-next-line no-alert
    alert(message);
  }

  dispatchCookieSettingsEvent(settings) {
    const event = new CustomEvent('cookieSettingsSaved', {
      detail: { settings },
    });
    document.dispatchEvent(event);
  }

  hideCookiePanel() {
    this.domElements.cookiePanel.classList.add('_hide');
    this.domElements.cookiePanel.classList.remove('_show');

    // Разблокируем скролл при скрытии панели
    document.body.style.overflow = '';
    document.body.style.height = '';

    if (this.isOpened) {
      this.closeSettings();
    }
  }

  async showPanel() {
    await this.updateCheckboxState();
    this.domElements.cookiePanel.classList.add('_show');
    this.domElements.cookiePanel.classList.remove('_hide');
  }

  async shouldShowPanel() {
    try {
      const { hasUserSettings } = await this.fetchSettingsFromServer();
      return !hasUserSettings;
    } catch (error) {
      this.handleError('Ошибка проверки настроек на сервере', error);
      // В случае ошибки показываем панель
      return true;
    }
  }

  // Метод для очистки всех таймаутов при уничтожении экземпляра
  destroy() {
    this.clearAllTimeouts();
  }
}

// Инициализация при загрузке DOM
document.addEventListener('DOMContentLoaded', () => {
  window.CookiePanel = new CookiePanel();
});